import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Pill, AlertTriangle, Clock, Shield, Brain,
  Activity, Heart, CheckCircle2, Info, Utensils, Dumbbell,
  BookOpen, FlaskConical, Stethoscope, Zap, Sparkles, Scale
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';

const PCOS_PROTOCOL = [
  {
    name: 'Myo-Inositol + D-Chiro-Inositol',
    dosage: '4,000mg Myo-Inositol + 100mg D-Chiro-Inositol (40:1 ratio)',
    timing: 'Split into 2 doses: morning and evening',
    icon: Sparkles,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    evidence: 'A',
    why: 'Inositol is the #1 evidence-based supplement for PCOS. The 40:1 Myo:D-Chiro ratio mirrors the body\'s natural ratio. Multiple RCTs show it improves insulin sensitivity, restores ovulation (62-72% of cases), reduces testosterone, and improves egg quality. Often called "the PCOS supplement."',
    interactions: ['Safe with Metformin (may allow dose reduction over time)', 'Safe with birth control pills', 'May enhance effects of fertility medications'],
    notes: 'Ovasitol® is the most studied brand with the 40:1 ratio. Powder form mixed in water is best absorbed. Effects visible in 3-6 months.'
  },
  {
    name: 'Berberine',
    dosage: '500mg, 2-3 times daily',
    timing: 'With meals',
    icon: FlaskConical,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'A',
    why: 'Berberine is as effective as Metformin for PCOS metabolic symptoms in head-to-head clinical trials. Activates AMPK, improves insulin sensitivity, lowers testosterone, supports weight loss, and improves lipid profiles. Some studies show better tolerability than Metformin.',
    interactions: ['Do NOT combine with Metformin without doctor approval (additive blood sugar lowering)', 'May interact with CYP3A4 medications', 'Take separately from other medications by 2+ hours'],
    notes: 'If you\'re on Metformin, discuss with your doctor before adding Berberine — you may be able to reduce Metformin dose. Dihydroberberine (GlucoVantage®) has 5x better absorption.'
  },
  {
    name: 'Vitamin D3',
    dosage: '2,000-5,000 IU daily',
    timing: 'Morning with breakfast (fat-containing meal)',
    icon: Heart,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
    evidence: 'A',
    why: '67-85% of PCOS patients are Vitamin D deficient. Deficiency worsens insulin resistance, inflammation, and hormonal imbalance. Supplementation improves menstrual regularity, insulin sensitivity, and AMH levels (fertility marker). Also supports mood — important as PCOS has high anxiety/depression rates.',
    interactions: ['Safe with all PCOS medications', 'Take with K2 for calcium metabolism', 'May enhance Metformin effectiveness'],
    notes: 'Get tested and aim for 40-60 ng/mL. Take with K2 (MK-7, 100mcg). Most PCOS patients need 4,000-5,000 IU to reach optimal levels.'
  },
  {
    name: 'Omega-3 (High EPA)',
    dosage: '2,000-3,000mg combined EPA/DHA',
    timing: 'With meals containing fat',
    icon: Heart,
    color: 'bg-sky-100 text-sky-700 border-sky-200',
    evidence: 'A',
    why: 'PCOS is driven by chronic low-grade inflammation. Omega-3s (especially EPA) reduce CRP, IL-6, and TNF-alpha — the inflammatory markers elevated in PCOS. Studies show improved insulin sensitivity, lower triglycerides, reduced testosterone, and better mood.',
    interactions: ['Safe with all PCOS medications', 'May enhance blood thinners', 'Pairs well with Vitamin D for anti-inflammatory synergy'],
    notes: 'Choose high-EPA formula (at least 60% EPA). IFOS-certified for purity. Algae-based for vegetarians.'
  },
  {
    name: 'NAC (N-Acetyl Cysteine)',
    dosage: '600-1,800mg daily',
    timing: 'Morning and evening, before meals',
    icon: Shield,
    color: 'bg-green-100 text-green-700 border-green-200',
    evidence: 'A',
    why: 'NAC improves insulin sensitivity and reduces testosterone in PCOS. A landmark study showed NAC was as effective as Metformin for improving insulin and lipid profiles. Also improves ovulation rates and egg quality. Powerful antioxidant that replenishes glutathione.',
    interactions: ['Safe with Metformin', 'Safe with Clomid/Letrozole (may enhance ovulation rates)', 'May enhance blood thinners slightly'],
    notes: 'Start with 600mg twice daily. NAC is particularly beneficial for PCOS patients who are trying to conceive. Take on empty stomach for best absorption.'
  },
  {
    name: 'Magnesium Glycinate',
    dosage: '300-400mg daily',
    timing: 'Evening, before bed',
    icon: Activity,
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    evidence: 'B+',
    why: 'Magnesium deficiency is common in PCOS and worsens insulin resistance. Supplementation improves insulin sensitivity, reduces inflammation, helps with anxiety and sleep (both major PCOS concerns), and reduces menstrual cramping.',
    interactions: ['Safe with all PCOS medications', 'Separate from antibiotics by 2+ hours', 'May enhance effects of blood pressure medications'],
    notes: 'Glycinate form is calming (helps with PCOS-related anxiety and insomnia). Avoid oxide form — poorly absorbed.'
  },
  {
    name: 'Zinc',
    dosage: '25-30mg daily',
    timing: 'With meals',
    icon: Zap,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'B+',
    why: 'Zinc is a natural 5-alpha reductase inhibitor — it blocks the conversion of testosterone to the more potent DHT (the hormone that causes PCOS hair loss and acne). Also supports insulin signaling, reduces inflammation, and improves ovarian function.',
    interactions: ['Safe with PCOS medications', 'Separate from iron by 2 hours', 'Long-term use requires copper supplementation (2mg Cu per 30mg Zn)'],
    notes: 'Zinc Picolinate for best absorption. Particularly helpful for PCOS-related acne, hair thinning, and hirsutism. Always pair with copper long-term.'
  },
  {
    name: 'Spearmint Tea / Extract',
    dosage: '2 cups of tea daily or 500mg extract',
    timing: 'Morning and evening',
    icon: Sparkles,
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    evidence: 'B',
    why: 'Spearmint has significant anti-androgen effects. A 30-day RCT showed spearmint tea reduced free testosterone by 29% and increased LH and FSH. Helps with hirsutism (excess hair growth), acne, and hormonal acne. One of the gentlest anti-androgen options.',
    interactions: ['Safe with all PCOS medications', 'Safe with Spironolactone (complementary anti-androgen effect)', 'May slightly enhance iron absorption'],
    notes: 'Organic spearmint tea is the simplest approach. Brew strong (steep 10+ minutes). Results for hirsutism take 3-6 months. Can also use spearmint essential oil capsules.'
  }
];

const PCOS_MEDS = [
  { name: 'Metformin', notes: 'Berberine is the biggest interaction risk — both lower blood sugar. If adding Berberine, discuss reducing Metformin. Inositol, NAC, Vitamin D, and Omega-3 are all safe and complementary.' },
  { name: 'Spironolactone (Aldactone)', notes: 'Used for anti-androgen effects. All supplements are safe. Zinc and Spearmint provide complementary anti-androgen support. Monitor potassium if combining with magnesium.' },
  { name: 'Birth Control Pills', notes: 'All supplements are safe. Inositol may help manage metabolic side effects of hormonal BC. Consider extra B-vitamins as BC depletes B6, B12, and folate.' },
  { name: 'Clomid / Letrozole (Fertility)', notes: 'Inositol and NAC may enhance ovulation rates when combined with fertility medications. Vitamin D optimization is critical before fertility treatment. CoQ10 can support egg quality.' },
  { name: 'GLP-1 Agonists (Ozempic, Wegovy)', notes: 'Increasingly used for PCOS weight management. All supplements are safe. Berberine provides complementary metabolic support. Monitor blood sugar closely when combining multiple insulin-sensitizing agents.' },
];

const LIFESTYLE_TIPS = [
  {
    icon: Utensils,
    title: 'Blood Sugar-Friendly Diet',
    content: 'Prioritize protein + healthy fat + fiber at every meal to prevent blood sugar spikes. The "PFF rule" — always pair carbs with Protein, Fat, and Fiber. Eat carbs LAST in your meal order (vegetables first, then protein, then carbs). This reduces glucose spikes by 40-75%.'
  },
  {
    icon: Dumbbell,
    title: 'Exercise Strategy',
    content: 'Strength training is the #1 exercise for PCOS — builds muscle which improves insulin sensitivity 24/7. Aim for 3-4 sessions/week. Add walking (8,000-10,000 steps/day) for daily blood sugar management. Avoid excessive cardio which can raise cortisol and worsen hormonal imbalance.'
  },
  {
    icon: Brain,
    title: 'Stress & Mental Health',
    content: 'PCOS women have 3x higher rates of anxiety and depression. Cortisol raises blood sugar and testosterone. Prioritize stress management: sleep 7-9 hours, limit caffeine (raises cortisol), practice mindfulness, and seek support. Inositol and Magnesium both support mood.'
  },
  {
    icon: Scale,
    title: 'Weight Management',
    content: 'Even 5-10% weight loss can restore ovulation and dramatically improve symptoms. But PCOS makes weight loss harder due to insulin resistance. Focus on metabolic health, not just calories. Inositol + Berberine + strength training is the most effective combination for PCOS-specific weight management.'
  }
];

const EvidenceGrade = ({ grade }) => {
  const colors = {
    'A': 'bg-green-100 text-green-800 border-green-300',
    'B+': 'bg-blue-100 text-blue-800 border-blue-300',
    'B': 'bg-sky-100 text-sky-800 border-sky-300',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${colors[grade] || colors['B']}`}>
      Evidence: {grade}
    </span>
  );
};

export default function PCOSGuidePage() {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="pcos-guide-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="pcos-guide-page">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-pink-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">PCOS Support Guide</h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            Evidence-based supplement protocol for Polycystic Ovary Syndrome — targeting insulin resistance, hormonal balance, and inflammation.
          </p>
        </motion.div>

        {/* Disclaimer */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8" data-testid="pcos-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">Important Medical Disclaimer</p>
              <p className="text-xs text-amber-800">
                This guide is for educational purposes only. Always discuss supplement changes with your OB/GYN or endocrinologist, 
                especially if taking Metformin, fertility medications, or trying to conceive. Supplements support — but do not replace — medical treatment.
              </p>
            </div>
          </div>
        </motion.div>

        {/* PCOS Pillars */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-pink-50 border border-pink-200 rounded-xl p-3 text-center">
            <Activity className="w-5 h-5 text-pink-600 mx-auto mb-1" />
            <p className="text-xs font-semibold text-pink-900">Insulin Resistance</p>
            <p className="text-[10px] text-pink-700">Root cause in 70% of cases</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
            <Sparkles className="w-5 h-5 text-purple-600 mx-auto mb-1" />
            <p className="text-xs font-semibold text-purple-900">Hormonal Balance</p>
            <p className="text-[10px] text-purple-700">Androgens, LH, FSH</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
            <Shield className="w-5 h-5 text-orange-600 mx-auto mb-1" />
            <p className="text-xs font-semibold text-orange-900">Inflammation</p>
            <p className="text-[10px] text-orange-700">Chronic low-grade</p>
          </div>
        </motion.div>

        {/* Daily Schedule */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-border p-5 mb-8" data-testid="pcos-schedule">
          <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Optimized Daily Schedule
          </h3>
          <div className="space-y-3">
            {[
              { time: 'Morning (with breakfast)', supps: 'Inositol (1st dose), Vitamin D3, Omega-3, NAC, Spearmint tea', color: 'bg-amber-50 border-amber-200 text-amber-900' },
              { time: 'Lunch', supps: 'Berberine, Zinc (with food)', color: 'bg-sky-50 border-sky-200 text-sky-900' },
              { time: 'Afternoon/Dinner', supps: 'Berberine (2nd dose), Omega-3 (2nd dose)', color: 'bg-orange-50 border-orange-200 text-orange-900' },
              { time: 'Evening (before bed)', supps: 'Inositol (2nd dose), Magnesium Glycinate, Spearmint tea', color: 'bg-indigo-50 border-indigo-200 text-indigo-900' }
            ].map((slot, i) => (
              <div key={i} className={`rounded-xl border p-3 ${slot.color}`}>
                <p className="text-xs font-semibold">{slot.time}</p>
                <p className="text-xs mt-1">{slot.supps}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Supplement Protocol */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            PCOS Supplement Protocol
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            8 evidence-based supplements targeting the 3 pillars of PCOS. Inositol is the cornerstone — start there and build.
          </p>

          <Accordion type="multiple" className="space-y-3">
            {PCOS_PROTOCOL.map((supp, index) => {
              const Icon = supp.icon;
              return (
                <motion.div key={supp.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index }}>
                  <AccordionItem value={supp.name} className="bg-white rounded-xl border border-border overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50/50">
                      <div className="flex items-center gap-3 text-left">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${supp.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-sm text-[#1A1A1A]">{supp.name}</h3>
                            <EvidenceGrade grade={supp.evidence} />
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{supp.dosage}</span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4 pt-2">
                        <div className="p-3 rounded-lg bg-pink-50 border border-pink-200">
                          <h4 className="text-sm font-medium text-pink-900 mb-1 flex items-center gap-2">
                            <FlaskConical className="w-4 h-4" />
                            Why This Matters for PCOS
                          </h4>
                          <p className="text-xs text-pink-800">{supp.why}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Medication Interactions
                          </h4>
                          <ul className="space-y-1">
                            {supp.interactions.map((int_, i) => (
                              <li key={i} className="text-xs text-blue-800 flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {int_}
                              </li>
                            ))}
                          </ul>
                        </div>
                        {supp.notes && (
                          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                            <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                              <Info className="w-4 h-4" />
                              Clinical Note
                            </h4>
                            <p className="text-xs text-gray-700">{supp.notes}</p>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              );
            })}
          </Accordion>
        </motion.div>

        {/* Medication Compatibility */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            Medication Compatibility
          </h2>
          <div className="space-y-3 mt-6" data-testid="pcos-med-compatibility">
            {PCOS_MEDS.map((med, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-4">
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">{med.name}</h4>
                <p className="text-xs text-muted-foreground">{med.notes}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Lifestyle */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            PCOS Lifestyle Strategies
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-testid="pcos-lifestyle">
            {LIFESTYLE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{tip.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Symptom Tracker CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-200 rounded-2xl p-6 mb-10 text-center"
          data-testid="pcos-tracker-cta">
          <h3 className="text-lg font-bold text-pink-900 mb-2">Track Your PCOS Symptoms</h3>
          <p className="text-sm text-pink-700 mb-4 max-w-md mx-auto">
            Log cycles, acne, energy, cravings, and mood to identify patterns and track supplement effectiveness.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-primary">
              <Link to="/symptoms">Start Symptom Tracking</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/journal">Open Journal</Link>
            </Button>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-3 mb-10">
          <Link to="/quiz" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Pill className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Get Personalized Protocol</p>
            <p className="text-xs text-muted-foreground mt-1">Take the full assessment</p>
          </Link>
          <Link to="/ask" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Brain className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Ask AI About PCOS</p>
            <p className="text-xs text-muted-foreground mt-1">Get supplement answers</p>
          </Link>
        </motion.div>
      </main>
      <GlobalFooter />
    </div>
  );
}
